import { Fragment, useState } from "react";
import { type AuthUser } from "wasp/auth";
import {
  createAffiliate,
  createAffiliateAsset,
  createAffiliateAssetUploadUrl,
  deleteAffiliateAsset,
  getAdminAffiliatePayouts,
  getAdminAffiliates,
  getAffiliateBrandingAssets,
  markAffiliatePayoutPaid,
  rejectAffiliatePayout,
  updateAffiliate,
  useAction,
  useQuery,
} from "wasp/client/operations";
import {
  Check,
  Download,
  Pencil,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import DefaultLayout from "../../layout/DefaultLayout";
import Breadcrumb from "../../layout/Breadcrumb";
import { Badge } from "../../../client/components/ui/badge";
import { Button } from "../../../client/components/ui/button";
import { Card, CardContent } from "../../../client/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../client/components/ui/dialog";
import { Input } from "../../../client/components/ui/input";
import { Label } from "../../../client/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../client/components/ui/select";
import { Switch } from "../../../client/components/ui/switch";
import { Textarea } from "../../../client/components/ui/textarea";
import { useToast } from "../../../client/hooks/use-toast";
import useTranslation from "../../../client/hooks/useTranslation";
import type { Translations } from "../../../client/i18n/translations";

type TabKey = "affiliates" | "payouts" | "assets";

function formatEur(amount: number): string {
  return `${amount.toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function formatRatePercent(rate: number): string {
  return `${Math.round(rate * 1000) / 10}`;
}

// ---------------------------------------------------------------------------
// Onglet Ambassadeurs
// ---------------------------------------------------------------------------

interface AffiliateRow {
  id: string;
  email: string | null;
  fullName: string | null;
  companyName: string | null;
  notes: string | null;
  refCode: string;
  commissionRate: number;
  commissionDurationMonths: number;
  isActive: boolean;
  clicksCount: number;
  referredCount: number;
  totalCommissions: number;
  balanceDue: number;
}

function AffiliateEditRow({
  affiliate,
  onDone,
}: {
  affiliate: AffiliateRow;
  onDone: () => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const update = useAction(updateAffiliate);
  const [ratePercent, setRatePercent] = useState(
    formatRatePercent(affiliate.commissionRate),
  );
  const [duration, setDuration] = useState(
    String(affiliate.commissionDurationMonths),
  );
  const [refCode, setRefCode] = useState(affiliate.refCode);
  const [companyName, setCompanyName] = useState(affiliate.companyName ?? "");
  const [notes, setNotes] = useState(affiliate.notes ?? "");
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await update({
        affiliateId: affiliate.id,
        commissionRate: Number(ratePercent) / 100,
        commissionDurationMonths: Number(duration),
        refCode: refCode.trim().toLowerCase(),
        companyName: companyName.trim() || null,
        notes: notes.trim() || null,
      });
      toast({ title: t.adminAffiliates.savedToast });
      onDone();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <tr className="border-b border-border bg-accent/30">
      <td colSpan={9} className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="space-y-1">
            <Label>{t.adminAffiliates.form.ratePercent}</Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={ratePercent}
              onChange={(e) => setRatePercent(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>{t.adminAffiliates.form.durationMonths}</Label>
            <Input
              type="number"
              min={1}
              max={120}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>{t.adminAffiliates.form.refCode}</Label>
            <Input
              value={refCode}
              onChange={(e) => setRefCode(e.target.value)}
              className="font-mono"
            />
          </div>
          <div className="space-y-1">
            <Label>{t.adminAffiliates.form.companyName}</Label>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>
          <div className="col-span-2 md:col-span-4 space-y-1">
            <Label>{t.adminAffiliates.form.notes}</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-3 flex gap-2 justify-end">
          <Button size="sm" variant="ghost" onClick={onDone}>
            <X className="h-3.5 w-3.5 mr-1" aria-hidden />
            {t.adminAffiliates.cancel}
          </Button>
          <Button size="sm" onClick={handleSave} disabled={isSaving}>
            <Check className="h-3.5 w-3.5 mr-1" aria-hidden />
            {t.adminAffiliates.save}
          </Button>
        </div>
      </td>
    </tr>
  );
}

function NewAffiliateDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) {
  const t = useTranslation();
  const { toast } = useToast();
  const create = useAction(createAffiliate);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [ratePercent, setRatePercent] = useState("20");
  const [duration, setDuration] = useState("12");
  const [refCode, setRefCode] = useState("");
  const [notes, setNotes] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const reset = () => {
    setEmail("");
    setFullName("");
    setCompanyName("");
    setRatePercent("20");
    setDuration("12");
    setRefCode("");
    setNotes("");
  };

  const handleCreate = async () => {
    setIsSubmitting(true);
    try {
      await create({
        email: email.trim(),
        fullName: fullName.trim(),
        companyName: companyName.trim() || undefined,
        commissionRate: Number(ratePercent) / 100,
        commissionDurationMonths: Number(duration),
        refCode: refCode.trim() ? refCode.trim().toLowerCase() : undefined,
        notes: notes.trim() || undefined,
      });
      toast({ title: t.adminAffiliates.createdToast });
      reset();
      onOpenChange(false);
      onCreated();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t.adminAffiliates.newAffiliate}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label>{t.adminAffiliates.form.email} *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>{t.adminAffiliates.form.fullName} *</Label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>{t.adminAffiliates.form.companyName}</Label>
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>{t.adminAffiliates.form.ratePercent}</Label>
            <Input
              type="number"
              min={1}
              max={100}
              value={ratePercent}
              onChange={(e) => setRatePercent(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label>{t.adminAffiliates.form.durationMonths}</Label>
            <Input
              type="number"
              min={1}
              max={120}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>{t.adminAffiliates.form.refCodeOptional}</Label>
            <Input
              value={refCode}
              onChange={(e) => setRefCode(e.target.value)}
              className="font-mono"
              placeholder={t.adminAffiliates.form.refCodePlaceholder}
            />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>{t.adminAffiliates.form.notes}</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          {t.adminAffiliates.createHint}
        </p>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t.adminAffiliates.cancel}
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isSubmitting || !email.trim() || !fullName.trim()}
          >
            {t.adminAffiliates.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AffiliatesTab() {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: affiliates, refetch } = useQuery(getAdminAffiliates);
  const update = useAction(updateAffiliate);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showNewDialog, setShowNewDialog] = useState(false);

  const handleToggleActive = async (affiliate: AffiliateRow) => {
    try {
      await update({ affiliateId: affiliate.id, isActive: !affiliate.isActive });
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex justify-end mb-4">
          <Button size="sm" onClick={() => setShowNewDialog(true)}>
            <Plus className="h-4 w-4 mr-1.5" aria-hidden />
            {t.adminAffiliates.newAffiliate}
          </Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-3">{t.adminAffiliates.cols.email}</th>
                <th className="py-2 pr-3">{t.adminAffiliates.cols.refCode}</th>
                <th className="py-2 pr-3 text-right">{t.adminAffiliates.cols.rate}</th>
                <th className="py-2 pr-3 text-right">{t.adminAffiliates.cols.duration}</th>
                <th className="py-2 pr-3 text-right">{t.adminAffiliates.cols.clicks}</th>
                <th className="py-2 pr-3 text-right">{t.adminAffiliates.cols.referred}</th>
                <th className="py-2 pr-3 text-right">{t.adminAffiliates.cols.commissions}</th>
                <th className="py-2 pr-3 text-right">{t.adminAffiliates.cols.balanceDue}</th>
                <th className="py-2 text-right">{t.adminAffiliates.cols.actions}</th>
              </tr>
            </thead>
            <tbody>
              {(affiliates ?? []).map((a: AffiliateRow) => (
                <Fragment key={a.id}>
                  <tr
                    className={`border-b border-border/50 ${a.isActive ? "" : "opacity-50"}`}
                  >
                    <td className="py-2.5 pr-3">
                      <div className="font-medium">{a.email}</div>
                      <div className="text-xs text-muted-foreground">
                        {[a.fullName, a.companyName].filter(Boolean).join(" · ")}
                      </div>
                    </td>
                    <td className="py-2.5 pr-3 font-mono text-xs">{a.refCode}</td>
                    <td className="py-2.5 pr-3 text-right font-mono tabular-nums">
                      {formatRatePercent(a.commissionRate)} %
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono tabular-nums">
                      {a.commissionDurationMonths}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono tabular-nums">
                      {a.clicksCount}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono tabular-nums">
                      {a.referredCount}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono tabular-nums">
                      {formatEur(a.totalCommissions)}
                    </td>
                    <td className="py-2.5 pr-3 text-right font-mono tabular-nums font-semibold">
                      {formatEur(a.balanceDue)}
                    </td>
                    <td className="py-2.5 text-right whitespace-nowrap">
                      <Switch
                        checked={a.isActive}
                        onCheckedChange={() => handleToggleActive(a)}
                        aria-label={t.adminAffiliates.cols.active}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-1"
                        onClick={() =>
                          setEditingId(editingId === a.id ? null : a.id)
                        }
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden />
                      </Button>
                    </td>
                  </tr>
                  {editingId === a.id && (
                    <AffiliateEditRow
                      affiliate={a}
                      onDone={() => {
                        setEditingId(null);
                        refetch();
                      }}
                    />
                  )}
                </Fragment>
              ))}
              {(affiliates ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="py-8 text-center text-muted-foreground"
                  >
                    {t.adminAffiliates.emptyAffiliates}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
      <NewAffiliateDialog
        open={showNewDialog}
        onOpenChange={setShowNewDialog}
        onCreated={refetch}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Onglet Payouts
// ---------------------------------------------------------------------------

function payoutStatusLabel(t: Translations, status: string): string {
  switch (status) {
    case "requested":
      return t.adminAffiliates.payoutStatus.requested;
    case "paid":
      return t.adminAffiliates.payoutStatus.paid;
    case "rejected":
      return t.adminAffiliates.payoutStatus.rejected;
    default:
      return status;
  }
}

function PayoutsTab() {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: payouts, refetch } = useQuery(getAdminAffiliatePayouts);
  const markPaid = useAction(markAffiliatePayoutPaid);
  const reject = useAction(rejectAffiliatePayout);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");

  const handleMarkPaid = async (payoutId: string) => {
    try {
      await markPaid({ payoutId });
      toast({ title: t.adminAffiliates.payoutPaidToast });
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    try {
      await reject({
        payoutId: rejectTarget,
        adminNotes: rejectNote.trim() || undefined,
      });
      toast({ title: t.adminAffiliates.payoutRejectedToast });
      setRejectTarget(null);
      setRejectNote("");
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="py-2 pr-3">{t.adminAffiliates.cols.date}</th>
                <th className="py-2 pr-3">{t.adminAffiliates.cols.email}</th>
                <th className="py-2 pr-3 text-right">{t.adminAffiliates.cols.amount}</th>
                <th className="py-2 pr-3">{t.adminAffiliates.cols.paymentDetails}</th>
                <th className="py-2 pr-3">{t.adminAffiliates.cols.status}</th>
                <th className="py-2 text-right">{t.adminAffiliates.cols.actions}</th>
              </tr>
            </thead>
            <tbody>
              {(payouts ?? []).map((p: any) => (
                <tr key={p.id} className="border-b border-border/50">
                  <td className="py-2.5 pr-3 whitespace-nowrap text-muted-foreground">
                    {new Date(p.createdAt).toLocaleDateString()}
                  </td>
                  <td className="py-2.5 pr-3">{p.affiliate.user.email}</td>
                  <td className="py-2.5 pr-3 text-right font-mono tabular-nums font-semibold">
                    {formatEur(p.amount)}
                  </td>
                  <td className="py-2.5 pr-3">
                    <div className="text-xs uppercase text-muted-foreground">
                      {p.affiliate.payoutMethod ?? "—"}
                    </div>
                    <div className="font-mono text-xs break-all">
                      {p.affiliate.payoutDetails ?? "—"}
                    </div>
                  </td>
                  <td className="py-2.5 pr-3">
                    <Badge
                      variant={
                        p.status === "paid"
                          ? "default"
                          : p.status === "rejected"
                            ? "destructive"
                            : "outline"
                      }
                    >
                      {payoutStatusLabel(t, p.status)}
                    </Badge>
                    {p.adminNotes && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {p.adminNotes}
                      </div>
                    )}
                  </td>
                  <td className="py-2.5 text-right whitespace-nowrap">
                    {p.status === "requested" && (
                      <>
                        <Button size="sm" onClick={() => handleMarkPaid(p.id)}>
                          <Check className="h-3.5 w-3.5 mr-1" aria-hidden />
                          {t.adminAffiliates.markPaid}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="ml-2"
                          onClick={() => setRejectTarget(p.id)}
                        >
                          <X className="h-3.5 w-3.5 mr-1" aria-hidden />
                          {t.adminAffiliates.reject}
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {(payouts ?? []).length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="py-8 text-center text-muted-foreground"
                  >
                    {t.adminAffiliates.emptyPayouts}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog
        open={!!rejectTarget}
        onOpenChange={(open) => !open && setRejectTarget(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t.adminAffiliates.rejectTitle}</DialogTitle>
          </DialogHeader>
          <div className="space-y-1">
            <Label>{t.adminAffiliates.form.adminNotes}</Label>
            <Textarea
              rows={3}
              value={rejectNote}
              onChange={(e) => setRejectNote(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectTarget(null)}>
              {t.adminAffiliates.cancel}
            </Button>
            <Button variant="destructive" onClick={handleReject}>
              {t.adminAffiliates.reject}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Onglet Assets branding
// ---------------------------------------------------------------------------

const ASSET_CATEGORIES = ["logo", "banner", "screenshot", "guidelines"] as const;

function AssetsTab() {
  const t = useTranslation();
  const { toast } = useToast();
  const { data: assets, refetch } = useQuery(getAffiliateBrandingAssets);
  const getUploadUrl = useAction(createAffiliateAssetUploadUrl);
  const createAsset = useAction(createAffiliateAsset);
  const deleteAsset = useAction(deleteAffiliateAsset);

  const [name, setName] = useState("");
  const [category, setCategory] = useState<string>("logo");
  const [sortOrder, setSortOrder] = useState("0");
  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const categoryLabel = (key: string) => {
    switch (key) {
      case "logo":
        return t.adminAffiliates.assetCategories.logo;
      case "banner":
        return t.adminAffiliates.assetCategories.banner;
      case "screenshot":
        return t.adminAffiliates.assetCategories.screenshot;
      case "guidelines":
        return t.adminAffiliates.assetCategories.guidelines;
      default:
        return key;
    }
  };

  const handleUpload = async () => {
    if (!file || !name.trim()) return;
    setIsUploading(true);
    try {
      const fileType = file.type || "application/octet-stream";
      const { s3UploadUrl, s3UploadFields, s3Key } = await getUploadUrl({
        fileName: file.name,
        fileType,
      });
      const formData = new FormData();
      Object.entries(s3UploadFields).forEach(([k, v]) =>
        formData.append(k, v as string),
      );
      formData.append("file", file);
      const uploadRes = await fetch(s3UploadUrl, {
        method: "POST",
        body: formData,
      });
      if (!uploadRes.ok) {
        throw new Error(t.adminAffiliates.uploadError);
      }
      await createAsset({
        name: name.trim(),
        category: category as (typeof ASSET_CATEGORIES)[number],
        s3Key,
        sortOrder: Number(sortOrder) || 0,
      });
      toast({ title: t.adminAffiliates.assetCreatedToast });
      setName("");
      setFile(null);
      setSortOrder("0");
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (assetId: string) => {
    try {
      await deleteAsset({ assetId });
      toast({ title: t.adminAffiliates.assetDeletedToast });
      refetch();
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div className="space-y-1">
              <Label>{t.adminAffiliates.form.assetName}</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label>{t.adminAffiliates.form.assetCategory}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ASSET_CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {categoryLabel(c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>{t.adminAffiliates.form.sortOrder}</Label>
              <Input
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label>{t.adminAffiliates.form.file}</Label>
              <Input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
            <Button
              onClick={handleUpload}
              disabled={isUploading || !file || !name.trim()}
            >
              <Upload className="h-4 w-4 mr-1.5" aria-hidden />
              {t.adminAffiliates.upload}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          {(assets ?? []).length === 0 ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              {t.adminAffiliates.emptyAssets}
            </p>
          ) : (
            <ul className="divide-y divide-border/50">
              {(assets ?? []).map((asset: any) => (
                <li
                  key={asset.id}
                  className="flex items-center justify-between gap-3 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{asset.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {categoryLabel(asset.category)} · #{asset.sortOrder}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button asChild size="sm" variant="ghost">
                      <a
                        href={asset.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Download className="h-3.5 w-3.5" aria-hidden />
                      </a>
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(asset.id)}
                    >
                      <Trash2 className="h-3.5 w-3.5 text-destructive" aria-hidden />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function AdminAffiliatesPage({ user }: { user: AuthUser }) {
  const t = useTranslation();
  const [tab, setTab] = useState<TabKey>("affiliates");

  const tabs: { key: TabKey; label: string }[] = [
    { key: "affiliates", label: t.adminAffiliates.tabs.affiliates },
    { key: "payouts", label: t.adminAffiliates.tabs.payouts },
    { key: "assets", label: t.adminAffiliates.tabs.assets },
  ];

  return (
    <DefaultLayout user={user}>
      <Breadcrumb pageName={t.adminAffiliates.title} />
      <div className="flex gap-2 mb-4">
        {tabs.map((item) => (
          <Button
            key={item.key}
            size="sm"
            variant={tab === item.key ? "default" : "outline"}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </Button>
        ))}
      </div>
      {tab === "affiliates" && <AffiliatesTab />}
      {tab === "payouts" && <PayoutsTab />}
      {tab === "assets" && <AssetsTab />}
    </DefaultLayout>
  );
}
